import { useMemo } from "react";

/**
 * CONSTELAÇÃO DE PESSOAS.
 *
 * O pano de fundo da entrada. Não é enfeite: é o que o produto faz, dito em
 * imagem. Nós são pessoas, arestas são vínculos, e o pulso que percorre a
 * aresta é a informação passando entre elas.
 *
 * Construída com SVG e CSS puros, sem biblioteca de animação. Numa tela de
 * login, cada quilobyte de JavaScript atrasa o primeiro campo ficar utilizável,
 * e ninguém veio aqui ver o gráfico.
 *
 * O vocabulário de movimento é o da casa: `edge-flow` (stroke-dashoffset
 * correndo na aresta) e `orb-breathe` vêm do EnturOS MKT.
 */
type No = { x: number; y: number; r: number; atraso: number; halo?: boolean };
type Aresta = { de: number; para: number; atraso: number; duracao: number };

/**
 * Posições FIXAS, não aleatórias.
 *
 * Layout sorteado a cada render dá uma composição diferente por visita, e
 * metade delas fica feia. Estas foram compostas: a rede ocupa a faixa
 * superior e a diagonal, e o CANTO INFERIOR ESQUERDO fica livre para a frase.
 * Texto em cima de nó é o erro que a primeira versão cometeu.
 *
 * Os nós são PEQUENOS de propósito. Nó grande vira planeta, e o desenho deixa
 * de ler como rede de gente para virar mancha.
 */
const NOS: No[] = [
  { x: 12, y: 22.7, r: 0.57, atraso: 260 },
  { x: 22, y: 36.9, r: 1.14, atraso: 0,   halo: true },
  { x: 34, y: 18.5, r: 0.68, atraso: 180 },
  { x: 31, y: 54.0, r: 0.83, atraso: 320 },
  { x: 45, y: 31.2, r: 1.35, atraso: 90,  halo: true },
  { x: 43, y: 65.3, r: 0.62, atraso: 420 },
  { x: 56, y: 46.9, r: 0.94, atraso: 240 },
  { x: 58, y: 17.0, r: 0.57, atraso: 500 },
  { x: 68, y: 34.1, r: 1.2, atraso: 140, halo: true },
  { x: 71, y: 66.7, r: 0.73, atraso: 380 },
  { x: 82, y: 24.1, r: 0.62, atraso: 560 },
  { x: 86, y: 51.1, r: 0.99, atraso: 300, halo: true },
  { x: 78, y: 82.4, r: 0.57, atraso: 620 },
  { x: 63, y: 88.0, r: 0.78, atraso: 460 },
  { x: 90, y: 88.0, r: 0.52, atraso: 700 },
  { x: 52, y: 80.9, r: 0.52, atraso: 540 },
  { x: 92, y: 66.7, r: 0.47, atraso: 660 },
  { x: 24, y: 66.7, r: 0.52, atraso: 600 },
  { x: 74, y: 100.8, r: 0.62, atraso: 760 },
  { x: 88, y: 107.9, r: 0.47, atraso: 820 },
];

const ARESTAS: Aresta[] = [
  { de: 0,  para: 1,  atraso: 0,    duracao: 3400 },
  { de: 1,  para: 2,  atraso: 500,  duracao: 3000 },
  { de: 1,  para: 3,  atraso: 900,  duracao: 3800 },
  { de: 2,  para: 4,  atraso: 300,  duracao: 3200 },
  { de: 3,  para: 4,  atraso: 1300, duracao: 4200 },
  { de: 4,  para: 6,  atraso: 700,  duracao: 3600 },
  { de: 4,  para: 7,  atraso: 1700, duracao: 3000 },
  { de: 6,  para: 8,  atraso: 200,  duracao: 3400 },
  { de: 6,  para: 5,  atraso: 2100, duracao: 4000 },
  { de: 7,  para: 8,  atraso: 1100, duracao: 3200 },
  { de: 8,  para: 10, atraso: 1500, duracao: 3600 },
  { de: 8,  para: 11, atraso: 400,  duracao: 3800 },
  { de: 8,  para: 9,  atraso: 2400, duracao: 4400 },
  { de: 11, para: 16, atraso: 1900, duracao: 3200 },
  { de: 9,  para: 12, atraso: 800,  duracao: 3600 },
  { de: 11, para: 12, atraso: 2600, duracao: 4000 },
  { de: 12, para: 14, atraso: 1200, duracao: 3400 },
  { de: 9,  para: 13, atraso: 2000, duracao: 4200 },
  { de: 13, para: 15, atraso: 600,  duracao: 3000 },
  { de: 3,  para: 17, atraso: 2200, duracao: 3800 },
  { de: 12, para: 18, atraso: 1600, duracao: 3600 },
  { de: 18, para: 19, atraso: 2800, duracao: 3200 },
  { de: 5,  para: 15, atraso: 1400, duracao: 4000 },
];

export function Constelacao({ className }: { className?: string }) {
  const arestas = useMemo(
    () => ARESTAS.map((a) => ({ ...a, a: NOS[a.de]!, b: NOS[a.para]! })), []);

  return (
    <svg
      className={`eop-constelacao ${className ?? ""}`}
      viewBox="0 0 100 132" preserveAspectRatio="xMidYMid slice"
      aria-hidden="true" focusable="false"
    >
      {/* A rede se dissolve na base, para os nós não virarem manchas marrons
          embaixo do véu que carrega a frase. */}
      <mask id="eop-c-mask">
        <rect x="0" y="0" width="100" height="132" fill="url(#eop-c-fade)" />
      </mask>
      <defs>
        {/* Halo discreto: 18% no centro e some rápido. A 50% ele virava
            neblina laranja e engolia a própria rede. */}
        <radialGradient id="eop-c-halo" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.18" />
          <stop offset="70%" stopColor="var(--primary)" stopOpacity="0.05" />
          <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="eop-c-fade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="1" />
          <stop offset="62%" stopColor="#fff" stopOpacity="1" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="eop-c-aresta" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--primary)" stopOpacity="0" />
          <stop offset="50%" stopColor="var(--primary)" stopOpacity="0.85" />
          <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* As arestas primeiro, para o nó sempre cobrir a linha que chega nele. */}
      <g className="eop-c-arestas" mask="url(#eop-c-mask)">
        {arestas.map((a, i) => (
          <g key={i}>
            <line
              x1={a.a.x} y1={a.a.y} x2={a.b.x} y2={a.b.y}
              stroke="var(--border-strong)" strokeWidth="0.2"
            />
            {/* O pulso: um traço curto correndo pela aresta. É o `edge-flow`
                da casa, que anima stroke-dashoffset em vez de mover um objeto. */}
            <line
              x1={a.a.x} y1={a.a.y} x2={a.b.x} y2={a.b.y}
              stroke="url(#eop-c-aresta)" strokeWidth="0.6" strokeLinecap="round"
              className="eop-c-pulso"
              style={{
                animationDelay: `${a.atraso}ms`,
                animationDuration: `${a.duracao}ms`,
              }}
            />
          </g>
        ))}
      </g>

      <g className="eop-c-nos" mask="url(#eop-c-mask)">
        {NOS.map((n, i) => (
          <g key={i} style={{ animationDelay: `${n.atraso}ms` }} className="eop-c-no">
            {/* Só os nós maiores ganham halo. Halo em todos vira mancha. */}
            {n.halo && (
              <circle cx={n.x} cy={n.y} r={n.r * 9} fill="url(#eop-c-halo)"
                      className="eop-c-halo"
                      style={{ animationDelay: `${n.atraso + 200}ms` }} />
            )}
            <circle cx={n.x} cy={n.y} r={n.r} fill="var(--primary)"
                    stroke="var(--canvas)" strokeWidth="0.28" />
          </g>
        ))}
      </g>
    </svg>
  );
}
